// src/lib/api/employerApi.ts

import { getApiClient } from './client';
import {
    Employer,
    EmployerWithDetails,
    Unit,
    Organisation,
    UpdateEmployerRequest,
    UpdateUnitRequest,
    UpdateOrganisationRequest,
    CreateEmployerRequest,
    CreateUnitRequest,
    CreateOrganisationRequest
} from '../models/employer';

// ===================================
// EMPLOYER OPERATIONS
// ===================================

/**
 * Get employer by ID with full details (user, unit, organisation)
 */
const getEmployerById = async (employerId: string): Promise<EmployerWithDetails> => {
    return getApiClient().get<EmployerWithDetails>(`/api/employers/${employerId}`);
};

/**
 * Get employer details for the current user
 * Uses the /api/employers/me endpoint which returns the employer profile
 * for the authenticated user based on their JWT token
 */
const getMyEmployerDetails = async (): Promise<EmployerWithDetails | null> => {
    try {
        console.log('[employerApi] Fetching current employer profile via /api/employers/me');
        const data = await getApiClient().get<EmployerWithDetails>('/api/employers/me');
        console.log('[employerApi] Employer profile loaded:', data.employer.id);
        return data;
    } catch (error: any) {
        // 404 means no employer profile exists yet (not an error)
        if (error?.status === 404 || error?.response?.status === 404) {
            console.log('[employerApi] No employer profile found (404)');
            return null;
        }

        // For other errors, log and return null
        console.error('[employerApi] Error fetching employer details:', error);
        return null;
    }
};

/**
 * Update employer profile
 */
const updateEmployer = async (
    employerId: string,
    data: UpdateEmployerRequest
): Promise<Employer> => {
    return getApiClient().put<Employer>(`/api/employers/${employerId}`, data);
};

/**
 * List all employers (admin only, but included for completeness)
 */
const listEmployers = async (): Promise<Employer[]> => {
    try {
        return await getApiClient().get<Employer[]>('/api/employers');
    } catch (error) {
        console.error('Error listing employers:', error);
        return [];
    }
};

// ===================================
// UNIT OPERATIONS
// ===================================

/**
 * Get unit by ID
 */
const getUnitById = async (unitId: string): Promise<Unit> => {
    return getApiClient().get<Unit>(`/api/units/${unitId}`);
};

/**
 * Update unit
 */
const updateUnit = async (
    unitId: string,
    data: UpdateUnitRequest
): Promise<Unit> => {
    return getApiClient().put<Unit>(`/api/units/${unitId}`, data);
};

/**
 * Get units by organisation ID
 */
const getUnitsByOrganisation = async (organisationId: string): Promise<Unit[]> => {
    try {
        return await getApiClient().get<Unit[]>(`/api/organisations/${organisationId}/units`);
    } catch (error) {
        console.error('Error fetching units:', error);
        return [];
    }
};

/**
 * List all units
 */
const listUnits = async (): Promise<Unit[]> => {
    try {
        return await getApiClient().get<Unit[]>('/api/units');
    } catch (error) {
        console.error('Error listing units:', error);
        return [];
    }
};

// ===================================
// ORGANISATION OPERATIONS (Read-only for employers)
// ===================================

/**
 * Get organisation by ID
 */
const getOrganisationById = async (organisationId: string): Promise<Organisation> => {
    return getApiClient().get<Organisation>(`/api/organisations/${organisationId}`);
};

/**
 * List all organisations
 */
const listOrganisations = async (): Promise<Organisation[]> => {
    try {
        return await getApiClient().get<Organisation[]>('/api/organisations');
    } catch (error) {
        console.error('Error listing organisations:', error);
        return [];
    }
};

// ===================================
// ADMIN-ONLY OPERATIONS (included for completeness)
// ===================================

/**
 * Create employer (admin only)
 */
const createEmployer = async (data: CreateEmployerRequest): Promise<EmployerWithDetails> => {
    return getApiClient().post<EmployerWithDetails>('/api/employers', data);
};

/**
 * Create unit (admin only)
 */
const createUnit = async (data: CreateUnitRequest): Promise<Unit> => {
    return getApiClient().post<Unit>('/api/units', data);
};

/**
 * Create organisation (admin only)
 */
const createOrganisation = async (data: CreateOrganisationRequest): Promise<Organisation> => {
    return getApiClient().post<Organisation>('/api/organisations', data);
};

/**
 * Update organisation (admin only)
 */
const updateOrganisation = async (
    organisationId: string,
    data: UpdateOrganisationRequest
): Promise<Organisation> => {
    return getApiClient().put<Organisation>(`/api/organisations/${organisationId}`, data);
};

/**
 * Delete employer (admin only)
 */
const deleteEmployer = async (employerId: string): Promise<void> => {
    await getApiClient().delete(`/api/employers/${employerId}`);
};

/**
 * Delete unit (admin only)
 */
const deleteUnit = async (unitId: string): Promise<void> => {
    await getApiClient().delete(`/api/units/${unitId}`);
};

/**
 * Delete organisation (admin only)
 */
const deleteOrganisation = async (organisationId: string): Promise<void> => {
    await getApiClient().delete(`/api/organisations/${organisationId}`);
};

export const employerApi = {
    // Employer operations
    getEmployerById,
    getMyEmployerDetails,
    updateEmployer,
    listEmployers,

    // Unit operations
    getUnitById,
    updateUnit,
    getUnitsByOrganisation,
    listUnits,

    // Organisation operations (read-only for employers)
    getOrganisationById,
    listOrganisations,

    // Admin operations
    createEmployer,
    createUnit,
    createOrganisation,
    updateOrganisation,
    deleteEmployer,
    deleteUnit,
    deleteOrganisation,
};
